import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@components/ui/Button';
import { Card } from '@components/ui/Card';
import {
  Cookie,
  Settings2,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  Shield,
  BarChart3,
  Megaphone,
  Cog,
} from 'lucide-react';

// Storage key for consent
const CONSENT_STORAGE_KEY = 'tremoria_cookie_consent';
const BANNER_VERSION = '1.0.0';

interface CookiePreferences {
  essential: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  consentGiven: boolean;
  consentGivenAt?: string;
  bannerVersion: string;
}

const DEFAULT_PREFERENCES: CookiePreferences = {
  essential: true,
  functional: true,
  analytics: true,
  marketing: true,
  consentGiven: false,
  bannerVersion: BANNER_VERSION,
};

interface CookieConsentBannerProps {
  onConsentGiven?: (preferences: CookiePreferences) => void;
}

export function CookieConsentBanner({ onConsentGiven }: CookieConsentBannerProps) {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(() => {
    // Initialize preferences from localStorage (render-time, runs only once)
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as CookiePreferences;
        if (parsed.consentGiven && parsed.bannerVersion === BANNER_VERSION) {
          return parsed;
        }
      } catch {
        // Invalid stored data
      }
    }
    return DEFAULT_PREFERENCES;
  });

  useEffect(() => {
    // Check if we should show the banner
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    let shouldShow = true;
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as CookiePreferences;
        if (parsed.consentGiven && parsed.bannerVersion === BANNER_VERSION) {
          shouldShow = false;
        }
      } catch {
        // Invalid stored data, show banner
      }
    }

    if (shouldShow) {
      // Show banner after a small delay for better UX
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (prefs: CookiePreferences) => {
    const toSave = {
      ...prefs,
      consentGiven: true,
      consentGivenAt: new Date().toISOString(),
      bannerVersion: BANNER_VERSION,
    };
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(toSave));
    setPreferences(toSave);
    setShowBanner(false);
    onConsentGiven?.(toSave);
  };

  const handleAcceptAll = () => {
    saveConsent({
      ...preferences,
      essential: true,
      functional: true,
      analytics: true,
      marketing: true,
    });
  };

  const handleRejectOptional = () => {
    saveConsent({
      ...preferences,
      essential: true,
      functional: false,
      analytics: false,
      marketing: false,
    });
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  const togglePreference = (key: keyof Omit<CookiePreferences, 'essential' | 'consentGiven' | 'consentGivenAt' | 'bannerVersion'>) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (!showBanner) {
    return null;
  }

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 p-4">
      <Card variant="elevated" className="mx-auto max-w-4xl border border-gray-200 shadow-xl dark:border-gray-700">
        <div className="p-4 sm:p-6">
          {/* Header */}
          <div className="mb-4 flex items-start gap-3">
            <div className="rounded-full bg-primary-100 p-2 dark:bg-primary-900/30">
              <Cookie className="h-6 w-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('cookies.banner.title', 'We use cookies')}
              </h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {t(
                  'cookies.banner.description',
                  'We use cookies to improve your experience, analyze site traffic, and provide personalized content. You can customize your preferences below.'
                )}
              </p>
            </div>
          </div>

          {/* Cookie Details (expandable) */}
          {showDetails && (
            <div className="mb-4 space-y-3 rounded-lg bg-gray-50 p-4 dark:bg-gray-800/50">
              {/* Essential Cookies */}
              <div className="flex items-start gap-3">
                <div className="mt-0.5 rounded-md bg-green-100 p-1.5 dark:bg-green-900/30">
                  <Shield className="h-4 w-4 text-green-600 dark:text-green-400" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900 dark:text-white">
                      {t('cookies.categories.essential.title', 'Essential')}
                    </span>
                    <span className="text-xs text-gray-500">
                      {t('cookies.alwaysActive', 'Always active')}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t(
                      'cookies.categories.essential.description',
                      'Required for the website to function. Cannot be disabled.'
                    )}
                  </p>
                </div>
              </div>

              {/* Functional Cookies */}
              <div className="flex items-start gap-3">
                <div className="mt-0.5 rounded-md bg-blue-100 p-1.5 dark:bg-blue-900/30">
                  <Cog className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900 dark:text-white">
                      {t('cookies.categories.functional.title', 'Functional')}
                    </span>
                    <label className="relative inline-flex cursor-pointer items-center">
                      <input
                        type="checkbox"
                        checked={preferences.functional}
                        onChange={() => togglePreference('functional')}
                        className="peer sr-only"
                      />
                      <div className="peer h-5 w-9 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary-600 peer-checked:after:translate-x-full dark:bg-gray-700"></div>
                    </label>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t(
                      'cookies.categories.functional.description',
                      'Enable enhanced features like language preferences and saved settings.'
                    )}
                  </p>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-start gap-3">
                <div className="mt-0.5 rounded-md bg-cyan-100 p-1.5 dark:bg-cyan-900/30">
                  <BarChart3 className="h-4 w-4 text-cyan-600 dark:text-cyan-400" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900 dark:text-white">
                      {t('cookies.categories.analytics.title', 'Analytics')}
                    </span>
                    <label className="relative inline-flex cursor-pointer items-center">
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={() => togglePreference('analytics')}
                        className="peer sr-only"
                      />
                      <div className="peer h-5 w-9 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary-600 peer-checked:after:translate-x-full dark:bg-gray-700"></div>
                    </label>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t(
                      'cookies.categories.analytics.description',
                      'Help us understand how visitors use our site to improve user experience.'
                    )}
                  </p>
                </div>
              </div>

              {/* Marketing Cookies */}
              <div className="flex items-start gap-3">
                <div className="mt-0.5 rounded-md bg-orange-100 p-1.5 dark:bg-orange-900/30">
                  <Megaphone className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900 dark:text-white">
                      {t('cookies.categories.marketing.title', 'Marketing')}
                    </span>
                    <label className="relative inline-flex cursor-pointer items-center">
                      <input
                        type="checkbox"
                        checked={preferences.marketing}
                        onChange={() => togglePreference('marketing')}
                        className="peer sr-only"
                      />
                      <div className="peer h-5 w-9 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary-600 peer-checked:after:translate-x-full dark:bg-gray-700"></div>
                    </label>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t(
                      'cookies.categories.marketing.description',
                      'Used to show relevant advertisements and measure campaign effectiveness.'
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
              >
                <Settings2 className="h-4 w-4" />
                {showDetails
                  ? t('cookies.hideDetails', 'Hide details')
                  : t('cookies.showDetails', 'Customize')}
                {showDetails ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </button>
              <span className="text-gray-300 dark:text-gray-600">|</span>
              <a
                href="/legal/privacy"
                className="text-sm text-primary-600 hover:underline dark:text-primary-400"
              >
                {t('cookies.privacyPolicy', 'Privacy Policy')}
              </a>
            </div>

            <div className="flex gap-2">
              {showDetails ? (
                <>
                  <Button variant="outline" size="sm" onClick={handleRejectOptional}>
                    <X className="mr-1.5 h-4 w-4" />
                    {t('cookies.rejectOptional', 'Essential only')}
                  </Button>
                  <Button size="sm" onClick={handleSavePreferences}>
                    <Check className="mr-1.5 h-4 w-4" />
                    {t('cookies.savePreferences', 'Save preferences')}
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" size="sm" onClick={handleRejectOptional}>
                    {t('cookies.rejectOptional', 'Essential only')}
                  </Button>
                  <Button size="sm" onClick={handleAcceptAll}>
                    <Check className="mr-1.5 h-4 w-4" />
                    {t('cookies.acceptAll', 'Accept all')}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

/**
 * Hook to get current cookie consent preferences
 */
export function useCookieConsent() {
  const [preferences, setPreferences] = useState<CookiePreferences | null>(() => {
    // Initialize from localStorage (render-time, runs only once)
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return null;
      }
    }
    return null;
  });

  const hasConsent = (type: 'essential' | 'functional' | 'analytics' | 'marketing'): boolean => {
    if (!preferences?.consentGiven) return type === 'essential';
    return preferences[type];
  };

  const resetConsent = () => {
    localStorage.removeItem(CONSENT_STORAGE_KEY);
    setPreferences(null);
    window.location.reload();
  };

  return { preferences, hasConsent, resetConsent };
}

/**
 * Button to reopen cookie settings (for footer/settings page)
 */
export function CookieSettingsButton({ className = '' }: { className?: string }) {
  const { t } = useTranslation();

  const reopenBanner = () => {
    localStorage.removeItem(CONSENT_STORAGE_KEY);
    window.location.reload();
  };

  return (
    <button
      onClick={reopenBanner}
      className={`inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 ${className}`}
    >
      <Cookie className="h-4 w-4" />
      {t('cookies.settings', 'Cookie Settings')}
    </button>
  );
}
